import sortBy from "lodash.sortby";
import { IKeyFigure } from "src/api/server/extended-search.types";
import { IKeyFigureValue } from "../components/types";

const MIN_QUERY_PARAM_NAME = "min";
const MAX_QUERY_PARAM_NAME = "max";

const getKeyFigureDefaultRange = (keyFigure: IKeyFigure) => ({
    defaultMinimum: keyFigure.steps[0],
    defaultMaximum: keyFigure.steps.at(-1)!,
});

const clampToRange = (value: number, min: number, max: number): number => {
    if (value < min) {
        return min;
    }
    if (value > max) {
        return max;
    }
    return value;
};

const parseKeyFigureValue = (keyFigureValue: string, keyFigure: IKeyFigure): IKeyFigureValue | null => {
    let minimum = Number.parseFloat(keyFigureValue.split(";")[0]);
    let maximum = Number.parseFloat(keyFigureValue.split(";")[1]);

    const { defaultMinimum, defaultMaximum } = getKeyFigureDefaultRange(keyFigure);

    minimum = clampToRange(minimum, defaultMinimum, defaultMaximum);
    maximum = clampToRange(maximum, defaultMinimum, defaultMaximum);

    if (minimum > maximum) {
        [minimum, maximum] = [maximum, minimum];
    }

    return {
        id: keyFigure.id,
        min: Number.isNaN(minimum) ? undefined : minimum,
        max: Number.isNaN(maximum) ? undefined : maximum,
        isPopular: keyFigure.isPopular ?? false,
    };
};

export const mapKeyFiguresFromQuery = (urlParams: URLSearchParams, keyFigures: readonly IKeyFigure[]): IKeyFigureValue[] => {
    const result: IKeyFigureValue[] = [];

    for (const keyFigure of keyFigures) {
        const keyFigureValue = urlParams.get(keyFigure.urlIdentifier);
        const keyFigureFound = keyFigureValue !== null && keyFigureValue.includes(";");

        if (keyFigureFound) {
            const parsedValue = parseKeyFigureValue(keyFigureValue, keyFigure);
            if (parsedValue) {
                result.push(parsedValue);
            }
        }
    }

    return result;
};

export const mapKeyFiguresToQuery = (keyFigures: readonly IKeyFigureValue[], keyFiguresData: readonly IKeyFigure[]) => {
    const selectedKeyFigures = [];

    for (const selectedKeyFigure of keyFigures ?? []) {
        const keyFigure = keyFiguresData.find(candidate => candidate.id === selectedKeyFigure.id);
        if (!keyFigure?.urlIdentifier) {
            continue;
        }

        const { defaultMinimum, defaultMaximum } = getKeyFigureDefaultRange(keyFigure);
        if (selectedKeyFigure.min !== defaultMinimum || selectedKeyFigure.max !== defaultMaximum) {
            selectedKeyFigures.push({
                urlIdentifier: keyFigure.urlIdentifier,
                min: selectedKeyFigure.min ?? MIN_QUERY_PARAM_NAME,
                max: selectedKeyFigure.max ?? MAX_QUERY_PARAM_NAME,
            });
        }
    }

    const sortedSelectedKeyFigures = sortBy(selectedKeyFigures, keyFigure => keyFigure.urlIdentifier);
    return sortedSelectedKeyFigures.map(keyFigure => `${keyFigure.urlIdentifier}=${keyFigure.min};${keyFigure.max}`).join("&");
};

export const markKeyFiguresWithPopularIndication = (keyFigures: readonly IKeyFigure[], popularKeyFigures: readonly IKeyFigure[]): IKeyFigure[] =>
    keyFigures.map(keyFigure => ({
        ...keyFigure,
        isPopular: popularKeyFigures.some(popularKeyFigure => popularKeyFigure.id === keyFigure.id),
    }));
